import { memo } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import CustomTooltip from '../../../Components/Tooltip/CustomTooltip';
import type { SubBomAttribute, SubBomCardAccess, SubBomCardHandlers } from './types';

const MAX_NAME_LENGTH = 50;

interface SubBomTextCardProps extends SubBomCardHandlers, SubBomCardAccess {
  item: SubBomAttribute;
  index: number;
  error?: string | undefined;
}

/**
 * One text attribute card. Markup, classes and labels mirror the BOM Custom
 * Attributes text card so the two masters read identically.
 *
 * Unlike that card, permissions arrive as props instead of being resolved from
 * the store inside every card, and the component is memoised — so typing in one
 * card does not re-render the rest of the list. The validation message sits
 * outside the flex row because `.freetext-container` is `align-items: flex-end`,
 * which would otherwise drag the action icons down onto the message line.
 * @param {SubBomTextCardProps} props - Card props.
 * @returns {ReactElement} The rendered card.
 */
function SubBomTextCard({ item, index, error, onNameChange, onToggleActive, onDelete, canEdit, canDelete }: SubBomTextCardProps): ReactElement {
  const { t } = useTranslation(['systemMaster', 'common']);

  return (
    <div className="d-flex flex-wrap mt-2 align-items-center justify-content-center">
      <div className="freetext-container mb-2 flex-fill">
        <div className="input-container">
          <div className="input-group form-floating-label fixed-label">
            <input
              name="freeLabel"
              autoFocus={!item?._id}
              value={item?.keyName ?? ''}
              placeholder={t('common:TEXT_CARD.FREE_TEXT_TITLE')}
              type="text"
              maxLength={MAX_NAME_LENGTH}
              onChange={(event) => onNameChange(index, event.target.value)}
              className="form-control"
              disabled={!canEdit}
            />
            <label className="asterisk-req">
              {t('common:TEXT_CARD.FREE_TEXT_LABEL')}
              <span>*</span>
            </label>
          </div>
        </div>
        <div className="input-action d-flex">
          {item?._id && canEdit && (
            <CustomTooltip placement="bottom" tooltipText={t('common:CUSTOM_MODULE.ACTIVE_INACTIVE')}>
              <button
                type="button"
                aria-label="active/inactive"
                className="btn btn-outline mr-0"
                onClick={() => onToggleActive(item._id as string, null, !item.isActive)}
              >
                <span className={item.isActive ? 'icon-Eye-Open' : 'icon-Eye-Closed'} />
              </button>
            </CustomTooltip>
          )}
          {canDelete && (
            <CustomTooltip placement="bottom" tooltipText={t('common:CUSTOM_ATTRIBUTES.DELETE')}>
              <button
                className="btn btn-outline mr-0"
                type="button"
                aria-label="Delete attribute"
                onClick={() => onDelete(index, item?._id ?? null, null)}
              >
                <span className="icon-Delete f-24" />
              </button>
            </CustomTooltip>
          )}
        </div>
      </div>
      {error && <label className="text-danger m-0 p-1 w-100">{t(error)}</label>}
    </div>
  );
}

export default memo(SubBomTextCard);
